import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { enableMapSet } from 'immer';
import { useImmer } from 'use-immer';

import { useBoardMembers } from '@trello/business-logic-react/board';
import { useBoardId } from '@trello/id-context';

import type { BoardMembersContextValue } from './BoardMembersContext';
import {
  BoardMembersContext,
  emptyBoardMembersContext,
} from './BoardMembersContext';
import { doSetsDiffer } from './doSetsDiffer';

export const BoardMembersContextProvider: FunctionComponent<
  PropsWithChildren<unknown>
> = ({ children }) => {
  const boardId = useBoardId();
  const [value, setValue] = useImmer<BoardMembersContextValue>(
    emptyBoardMembersContext,
  );

  const { members, isMemberDeactivated } = useBoardMembers(boardId);

  useEffect(() => {
    enableMapSet();
  }, []);

  useEffect(() => {
    if (!members.length) {
      return;
    }

    const deactivatedIds = new Set(
      members
        .filter((member) => {
          return isMemberDeactivated(member.id);
        })
        .map((member) => member.id),
    );

    setValue((cachedValue) => {
      if (!cachedValue.deactivatedIds) {
        return {
          deactivatedIds,
        };
      }

      // The order of the dactivated IDs is not important. So we don't need to
      // do a deepEqual check. We really just need to know if there are any
      // entries in one that are not present in the other. And if so, update
      // the cache with the new array.

      if (doSetsDiffer(cachedValue.deactivatedIds, deactivatedIds)) {
        cachedValue.deactivatedIds = deactivatedIds;
      }

      return cachedValue;
    });
  }, [isMemberDeactivated, members, setValue]);

  return (
    <BoardMembersContext.Provider value={value}>
      {children}
    </BoardMembersContext.Provider>
  );
};
